/**
 * The guided tours (owner 2026-10-31, thread "Föreslå guidad tur per sida", "Alla sidor på en gång"): 3–5 steps per page,
 * each telling WHY the thing is there, not only that it is. A tour starts the first time a person opens the page and
 * can be played again from Klara ("Show me around") or the command palette. tour.tsx runs them.
 *
 * - el:   the `data-tour` value of the element the step points at. A step whose element is not on the page (another
 *         role, an empty list, a module that is off) is left out; a tour with fewer than two steps left does not start.
 * - The ids are the API's too (apps/api/src/tours.ts), where "seen" is stored; a test keeps both lists and every `el`
 *   in step with the code.
 * - Role names: Redigering/Editing and Kamera/Camera, never "Klipp"/"Foto" (owner 2026-10-21).
 */
export interface TourStep {
  el: string;
  en: [title: string, text: string];
  sv: [title: string, text: string];
}

export type TourId =
  | 'home'
  | 'home-crew'
  | 'projects'
  | 'projects-crew'
  | 'project'
  | 'project-crew'
  | 'review'
  | 'review-crew'
  | 'team'
  | 'availability'
  | 'availability-own'
  | 'my-time'
  | 'clients'
  | 'deals'
  | 'contracts'
  | 'finance'
  | 'finance-invoicing'
  | 'finance-costs'
  | 'post'
  | 'post-accounts'
  | 'post-tips'
  | 'portal-review';

/** The last step on the start pages: where help (and this tour) is found again. */
const HELP: TourStep = {
  el: 'help',
  en: ['Stuck? Ask Klara', 'Ask a question in your own words here. This is also where you play a page’s tour again.'],
  sv: ['Fastnar du? Fråga Klara', 'Ställ en fråga med egna ord här. Här spelar du också upp guiden för en sida igen.']
};

const WEEK: TourStep = {
  el: 'week',
  en: ['Your week at a glance', 'Your own Google meetings, shoots and deadlines in one view. "+ Plan" under a day books time on one of your projects; ▶ on the block starts the timer.'],
  sv: ['Veckan på ett ögonblick', 'Dina egna Google-möten, inspelningar och deadlines i samma vy. "+ Planera" under en dag bokar tid på ett av dina projekt; ▶ på blocket startar timern.']
};

export const TOURS: Record<TourId, TourStep[]> = {
  home: [
    {
      el: 'work-turn',
      en: ['Your turn, first', 'Everything waiting on you, the most urgent at the top. The button on the row takes the next step, and you can undo it.'],
      sv: ['Din tur, först', 'Allt som väntar på dig, det som är mest bråttom överst. Knappen på raden tar nästa steg, och du kan ångra direkt.']
    },
    WEEK,
    {
      el: 'team-week',
      en: ['Who has time this week', 'See who is fully booked and who is free before you promise a client a date.'],
      sv: ['Vem har tid den här veckan', 'Se vem som är fullbokad och vem som har tid innan du lovar en kund ett datum.']
    },
    {
      el: 'search',
      en: ['Find anything with Ctrl K', 'Projects, clients, people and shortcuts, without hunting through the menu.'],
      sv: ['Hitta allt med Ctrl K', 'Projekt, kunder, personer och genvägar, utan att leta i menyn.']
    },
    HELP
  ],
  'home-crew': [
    {
      el: 'shoots',
      en: ['Your shoots', 'Today’s and the week’s shoots. Open one for the shoot details; the brief is one click away.'],
      sv: ['Dina inspelningar', 'Dagens och veckans inspelningar. Öppna en för detaljerna; briefen ligger ett klick bort.']
    },
    {
      el: 'work-turn',
      en: ['Your turn', 'What you do now: feedback to fix and drafts to send. The button on the row takes the next step.'],
      sv: ['Din tur', 'Det du ska göra nu: feedback att åtgärda och utkast att skicka. Knappen på raden tar nästa steg.']
    },
    WEEK,
    {
      el: 'guide',
      en: ['Getting started', 'A few things to do once, like your calendar and the project folder. Tick them off as you go.'],
      sv: ['Kom igång', 'Några saker att göra en gång, som kalendern och projektmappen. Bocka av allt eftersom.']
    },
    HELP
  ],
  projects: [
    {
      el: 'new-project',
      en: ['Start a project in a minute', 'Pick the client and the producer, type, package and number of videos are suggested from their last project.'],
      sv: ['Starta ett projekt på en minut', 'Välj kunden, så föreslås producent, typ, paket och antal videor från kundens senaste projekt.']
    },
    {
      el: 'project-scope',
      en: ['Mine or all', 'Start in your own projects. All is for when you cover for a colleague.'],
      sv: ['Mina eller alla', 'Börja i dina egna projekt. Alla är för när du täcker upp för en kollega.']
    },
    {
      el: 'project-views',
      en: ['One-click filters', 'Your turn, what is late, or what sits with the client, in one click.'],
      sv: ['Snabbfilter', 'Din tur, det som är försenat eller det som ligger hos kund, med ett klick.']
    },
    {
      el: 'month',
      en: ['A month at a time', 'Projects are grouped by delivery month. The arrow on a row shows its videos, and ⋯ has shortcuts such as the video’s next step.'],
      sv: ['En månad i taget', 'Projekten är grupperade efter leveransmånad. Pilen på raden visar videorna, och ⋯ har genvägar som videons nästa steg.']
    }
  ],
  'projects-crew': [
    {
      el: 'project-scope',
      en: ['Mine or all', 'Your own projects first. All shows what the rest of KFF is working on.'],
      sv: ['Mina eller alla', 'Dina egna projekt först. Alla visar vad resten av KFF jobbar med.']
    },
    {
      el: 'project-views',
      en: ['One-click filters', 'Your turn, late, within a week, in editing or with the client, in one click.'],
      sv: ['Snabbfilter', 'Din tur, försenat, inom en vecka, under redigering eller hos kund, med ett klick.']
    },
    {
      el: 'month',
      en: ['Month by month', 'The same list as everyone else’s. The arrow on a row shows its videos, and ⋯ has shortcuts: upload a draft, open the review, start the timer.'],
      sv: ['Månad för månad', 'Samma lista som alla andra har. Pilen på raden visar videorna, och ⋯ har genvägar: ladda upp utkast, öppna granskningen, starta timern.']
    }
  ],
  project: [
    {
      el: 'journey',
      en: ['Where the project stands', 'The whole journey from planning to delivery, and where you are right now.'],
      sv: ['Var projektet står', 'Hela resan från planering till leverans, och var ni är just nu.']
    },
    {
      el: 'now',
      en: ['What happens now', 'The next step and who it waits on. Book the shoot and add videos here.'],
      sv: ['Vad händer nu', 'Nästa steg och vem det väntar på. Här bokar du inspelning och lägger till videor.']
    },
    {
      el: 'videos',
      en: ['The videos', 'Every film with its status. The button on the card takes the next step, like sending it to the client.'],
      sv: ['Videorna', 'Varje film med sin status. Knappen på kortet tar nästa steg, till exempel att skicka till kund.']
    },
    {
      el: 'tabs',
      en: ['Everything about the project', 'Brief, Shoots, Files and Notes in the tabs, so the team finds the same thing you do.'],
      sv: ['Allt om projektet', 'Brief, Inspelningar, Filer och Anteckningar i flikarna, så att teamet hittar samma sak som du.']
    },
    {
      el: 'team-strip',
      en: ['The team', 'Who produces, edits and films. Hover for names; click a role to change who has it. The dashed ball means nobody yet.'],
      sv: ['Teamet', 'Vem som är producent, redigerar och filmar. Håll över för namn, klicka på en roll för att byta person. Den streckade bollen betyder att ingen har rollen än.']
    }
  ],
  'project-crew': [
    {
      el: 'tabs',
      en: ['Brief first, then Files', 'Read what the client wants and their brand (logo, colours, fonts, brand guide) before you start. Files opens the project folder at EXPORTS/VIDEO.'],
      sv: ['Briefen först, sedan Filer', 'Läs vad kunden vill ha och kolla deras brand (logga, färger, typsnitt, grafisk manual) innan du börjar. Filer öppnar projektmappen i EXPORTS/VIDEO.']
    },
    {
      el: 'now',
      en: ['What happens now', 'Who the project waits on right now, so you know when it is your turn.'],
      sv: ['Vad händer nu', 'Vem projektet väntar på just nu, så att du vet när det är din tur.']
    },
    {
      el: 'videos',
      en: ['Your videos', 'Status and next step for each film. The button on the card takes the step when it is your turn.'],
      sv: ['Dina videor', 'Status och nästa steg för varje film. Knappen på kortet tar steget när det är din tur.']
    },
    {
      el: 'team-strip',
      en: ['Who you work with', 'The producer is your contact for questions about the project. Hover for names.'],
      sv: ['Vem du jobbar med', 'Producenten är din kontakt för frågor om projektet. Håll över för namn.']
    }
  ],
  review: [
    {
      el: 'player',
      en: ['Comment in the picture', 'Pause and click where something should change. The comment lands on that exact frame, and its writer shows as a ball on the timeline.'],
      sv: ['Kommentera i bilden', 'Pausa och klicka där något ska ändras. Kommentaren hamnar på exakt den bildrutan, och den som skrev syns som en boll på tidslinjen.']
    },
    {
      el: 'comments',
      en: ['All comments', 'Click a timecode to jump there. Tick the round box when it is fixed; All shows the done ones too.'],
      sv: ['Alla kommentarer', 'Klicka på en tidskod för att hoppa dit. Bocka i rutan när det är åtgärdat; Alla visar även de klara.']
    },
    {
      el: 'versions',
      en: ['Versions', 'Switch version or compare with an earlier one, so you see the changes were made.'],
      sv: ['Versioner', 'Byt version eller jämför med en tidigare, så ser du att ändringarna är gjorda.']
    },
    {
      el: 'decide',
      en: ['Your next step', 'Always at the top right: send it on to the client or ask for changes. The next person in line hears about it right away.'],
      sv: ['Ditt nästa steg', 'Alltid överst till höger: skicka vidare till kunden eller be om ändringar. Nästa person i kedjan får veta det direkt.']
    }
  ],
  'review-crew': [
    {
      el: 'send-version',
      en: ['Send a new version', 'Upload your export here. It becomes the next version of the same video, never a new one.'],
      sv: ['Skicka ny version', 'Ladda upp exporten här. Den blir nästa version av samma video, aldrig en ny.']
    },
    {
      el: 'comments',
      en: ['The comments to fix', 'Click the timecode to jump there, and tick it off when it is fixed.'],
      sv: ['Kommentarerna att åtgärda', 'Klicka på tidskoden för att hoppa dit, och bocka av när det är fixat.']
    },
    {
      el: 'player',
      en: ['Right on the frame', 'Each comment sits on its frame. Click in the picture to reply exactly there.'],
      sv: ['Exakt på bildrutan', 'Varje kommentar sitter på sin bildruta. Klicka i bilden för att svara exakt där.']
    },
    {
      el: 'panel',
      en: ['The Premiere panel', 'The same comments inside Premiere, added as markers on your timeline.'],
      sv: ['Premiere-panelen', 'Samma kommentarer inne i Premiere, inlagda som markörer i tidslinjen.']
    },
    {
      el: 'versions',
      en: ['Versions', 'Compare with the previous version before you send the next one.'],
      sv: ['Versioner', 'Jämför med förra versionen innan du skickar nästa.']
    }
  ],
  team: [
    {
      el: 'team-stats',
      en: ['The state of things', 'Who is over-booked, how much time is free, and which freelancers have not given their availability.'],
      sv: ['Läget på en rad', 'Vem som är överbokad, hur mycket tid som är ledig och vilka frilansare som inte angett tillgänglighet.']
    },
    {
      el: 'team-grid',
      en: ['Hours per week', 'Booked against available time for each person. Click a week to see what is in it.'],
      sv: ['Timmar per vecka', 'Bokad tid mot tillgänglig tid för varje person. Klicka på en vecka för att se vad som ligger där.']
    },
    {
      el: 'team-legend',
      en: ['Read the colours', 'Under 85 % has room, 85–100 % is nearly full, over 100 % is over-booked.'],
      sv: ['Läs färgerna', 'Under 85 % har plats, 85–100 % är nästan fullt, över 100 % är överbokat.']
    },
    {
      el: 'team-roles',
      en: ['One role at a time', 'Show only Editing or Camera when you are looking for someone.'],
      sv: ['En roll i taget', 'Visa bara Redigering eller Kamera när du letar efter någon.']
    }
  ],
  availability: [
    {
      el: 'av-months',
      en: ['Pick the month', 'This month and the coming ones, as the freelancers have given them.'],
      sv: ['Välj månad', 'Den här och kommande månader, som frilansarna har angett dem.']
    },
    {
      el: 'av-table',
      en: ['Every freelancer', 'How much each one can work per week, and who has not sent theirs yet.'],
      sv: ['Varje frilansare', 'Hur mycket var och en kan jobba per vecka, och vem som inte skickat in än.']
    },
    {
      el: 'av-remind',
      en: ['Remind', 'One click reminds those who have not answered.'],
      sv: ['Påminn', 'Ett klick påminner dem som inte har svarat.']
    }
  ],
  'availability-own': [
    {
      el: 'av-weeks',
      en: ['Your weeks', 'How much you can work each week, in percent or hours. The producers plan your work from it.'],
      sv: ['Dina veckor', 'Hur mycket du kan jobba varje vecka, i procent eller timmar. Producenterna planerar ditt arbete utifrån det.']
    },
    {
      el: 'av-same',
      en: ['Same as last month', 'One click fills it all in. Check it and send.'],
      sv: ['Samma som förra månaden', 'Ett klick fyller i allt. Kolla och skicka.']
    },
    {
      el: 'av-send',
      en: ['Send', 'You can change it any time; the producers always see the latest.'],
      sv: ['Skicka', 'Du kan ändra när som helst; producenterna ser alltid det senaste.']
    }
  ],
  'my-time': [
    {
      el: 'timer',
      en: ['The timer', 'Start it on the project you are working on. It sits at the top of every page.'],
      sv: ['Timern', 'Starta den på projektet du jobbar med. Den finns överst på varje sida.']
    },
    {
      el: 'time-add',
      en: ['Add it afterwards', 'Forgot the timer? Add time for any day here.'],
      sv: ['Lägg till i efterhand', 'Glömde du timern? Lägg till tid för valfri dag här.']
    },
    {
      el: 'time-week',
      en: ['Week by week', 'Everything you logged, day by day. The arrows take you to earlier weeks.'],
      sv: ['Vecka för vecka', 'Allt du loggat, dag för dag. Pilarna tar dig till tidigare veckor.']
    }
  ],
  clients: [
    {
      el: 'client-stats',
      en: ['What needs you', 'Agreements to renew, clients waiting and clients gone quiet. Click a figure to filter on it.'],
      sv: ['Det som behöver dig', 'Avtal att förnya, kunder som väntar och kunder som blivit tysta. Klicka på en siffra för att filtrera.']
    },
    {
      el: 'client-search',
      en: ['Search', 'By name or code.'],
      sv: ['Sök', 'På namn eller kod.']
    },
    {
      el: 'client-list',
      en: ['The client card', 'Open a client for contacts, agreements and projects. Contact details are changed there, so nobody types them twice.'],
      sv: ['Kundkortet', 'Öppna en kund för kontakter, avtal och projekt. Kontaktuppgifter ändras där, så att ingen skriver in dem två gånger.']
    }
  ],
  deals: [
    {
      el: 'new-deal',
      en: ['A new deal', 'Add it as soon as you have spoken to the client, so the team sees what is coming.'],
      sv: ['En ny affär', 'Lägg in den direkt när du pratat med kunden, så att teamet ser vad som är på väg.']
    },
    {
      el: 'deal-cols',
      en: ['From quote to won', 'Each card is a quote. Drag it on when it is sent, won or lost. The sum shows the value in each column.'],
      sv: ['Från offert till vunnen', 'Varje kort är en offert. Dra den vidare när den skickas, vinns eller förloras. Summan visar värdet i varje kolumn.']
    },
    {
      el: 'deal-scope',
      en: ['All or mine', 'The whole pipeline, or only the deals you own.'],
      sv: ['Alla eller mina', 'Hela flödet, eller bara de affärer du äger.']
    }
  ],
  contracts: [
    {
      el: 'contract-filters',
      en: ['In force, renew soon, ended', 'Start with Renew soon: those are the ones to talk to the client about.'],
      sv: ['Gällande, förnyas snart, avslutade', 'Börja med Förnyas snart: det är dem du ska prata med kunden om.']
    },
    {
      el: 'signing',
      en: ['Out for signing', 'Agreements waiting for the client’s e-signature.'],
      sv: ['Ute för signering', 'Avtal som väntar på kundens e-signatur.']
    },
    {
      el: 'contracts-table',
      en: ['Every agreement', 'Open one for its terms, dates and the signed PDF.'],
      sv: ['Alla avtal', 'Öppna ett för villkor, datum och den signerade PDF:en.']
    }
  ],
  finance: [
    {
      el: 'tabs',
      en: ['One tab per task', 'Overview, To invoice, Costs and Margins. Each tab is one job.'],
      sv: ['En flik per uppgift', 'Översikt, Att fakturera, Kostnader och Marginaler. Varje flik är ett jobb.']
    },
    {
      el: 'fin-stats',
      en: ['The figures', 'Click a figure to see the invoices behind it.'],
      sv: ['Siffrorna', 'Klicka på en siffra för att se fakturorna bakom den.']
    },
    {
      el: 'fin-filters',
      en: ['Narrow it down', 'Limit everything to one client or one period.'],
      sv: ['Smalna av', 'Begränsa allt till en kund eller en period.']
    }
  ],
  'finance-invoicing': [
    {
      el: 'fin-month',
      en: ['The month', 'What is to be invoiced for the month. The arrows step between months.'],
      sv: ['Månaden', 'Det som ska faktureras för månaden. Pilarna bläddrar mellan månader.']
    },
    {
      el: 'fin-approve',
      en: ['Approve into Bokio', 'Confirmed invoice bases go to Bokio in one click.'],
      sv: ['Godkänn till Bokio', 'Bekräftade underlag går till Bokio med ett klick.']
    },
    {
      el: 'fin-basis',
      en: ['The invoice bases', 'One row per client. Open it and check it before you approve.'],
      sv: ['Underlagen', 'En rad per kund. Öppna och kontrollera innan du godkänner.']
    }
  ],
  'finance-costs': [
    {
      el: 'costs-filter',
      en: ['To do first', 'Supplier invoices waiting for your approval.'],
      sv: ['Att göra först', 'Leverantörsfakturor som väntar på din attest.']
    },
    {
      el: 'costs-table',
      en: ['Approve', 'Check the cost against the project and approve it, or dispute it with a note. Payment stays in Bokio.'],
      sv: ['Attestera', 'Kontrollera kostnaden mot projektet och godkänn, eller invänd med en kommentar. Betalningen sker i Bokio.']
    }
  ],
  // Post production (owner 2026-11-03): the step-by-step guide for Redigering and Kamera
  post: [
    {
      el: 'post-start',
      en: ['A guide you click through', 'One step at a time: how we get the material, edit, export and deliver. Stop when you like, it remembers where you are.'],
      sv: ['En guide du klickar dig igenom', 'Ett steg i taget: hur vi hämtar materialet, klipper, exporterar och levererar. Pausa när du vill, den kommer ihåg var du är.']
    },
    {
      el: 'post-steps',
      en: ['Jump to a step', 'Need just the export names or the sound levels? Open that step straight away.'],
      sv: ['Hoppa till ett steg', 'Behöver du bara exportnamnen eller ljudnivåerna? Öppna det steget direkt.']
    },
    HELP
  ],
  // Post production › Accounts: the shared logins, encrypted
  'post-accounts': [
    {
      el: 'post-accounts',
      en: ['Our shared logins', 'Copy puts the password on your clipboard and empties it after 30 seconds. Every time a password is shown or copied, it is logged.'],
      sv: ['Våra delade inloggningar', 'Kopiera lägger lösenordet i urklipp och tömmer det efter 30 sekunder. Varje gång ett lösenord visas eller kopieras loggas det.']
    },
    HELP
  ],
  // Post production: one overview page (owner 2026-11-03), tips, accounts and tools; the guide behind a button
  'post-tips': [
    {
      el: 'post-tips',
      en: ['Share what you learn', 'A tip, a lesson from a project or a way of working. Everyone in Editing and Camera sees it, freelancers too. 👍 tells the author it helped.'],
      sv: ['Dela det du lär dig', 'Ett tips, en lärdom från ett projekt eller ett arbetssätt. Alla i Redigering och Kamera ser det, även frilansare. 👍 visar att det hjälpte.']
    },
    {
      el: 'post-news',
      en: ['New since last time', 'What colleagues shared since you were last here, and accounts that changed. Below: shortcuts to what you use most.'],
      sv: ['Nytt sedan sist', 'Det kollegorna delat sedan du var här sist, och konton som ändrats. Under: genvägar till det du använder mest.']
    },
    {
      el: 'post-accounts',
      en: ['Our shared logins', 'Copy puts the password on your clipboard and empties it after 30 seconds. Every time a password is shown or copied, it is logged.'],
      sv: ['Våra delade inloggningar', 'Kopiera lägger lösenordet i urklipp och tömmer det efter 30 sekunder. Varje gång ett lösenord visas eller kopieras loggas det.']
    },
    {
      el: 'post-guide',
      en: ['Step by step', 'New here? The guide walks you through how we work, one step at a time. It never starts by itself.'],
      sv: ['Steg för steg', 'Ny här? Guiden går igenom hur vi jobbar, ett steg i taget. Den startar aldrig av sig själv.']
    },
    HELP
  ],
  // the client portal's review page; seen-state in the browser (contacts are not people in the Hub)
  'portal-review': [
    {
      el: 'player',
      en: ['Comment right in the film', 'Pause and click where something should change. Your comment lands on that exact frame.'],
      sv: ['Kommentera direkt i filmen', 'Pausa och klicka där något ska ändras. Kommentaren hamnar på exakt den bildrutan.']
    },
    {
      el: 'comments',
      en: ['Your comments', 'Everything you and your colleagues wrote, with the time in the film.'],
      sv: ['Era kommentarer', 'Allt du och dina kollegor skrivit, med tiden i filmen.']
    },
    {
      el: 'decide',
      en: ['Approve or ask for changes', 'One decision per version, and we hear about it straight away.'],
      sv: ['Godkänn eller be om ändringar', 'Ett beslut per version, och vi får veta det direkt.']
    }
  ]
};
